import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const SECTION_MAP = {
  "/": "./HOME",
  "/work": "./WORK",
  "/gallery": "./GALLERY",
  "/contact": "./CONTACT",
};

/* ── Cinematic curtain transition ─────────────────────
   entering:  black curtain falls top→bottom   (500ms)
   active:    section text clip-path reveals   (600ms)
   exiting:   curtain + text slide up bottom→top (500ms)
───────────────────────────────────────────────────── */

export default function TransitionLoader({ pathname, onDone }) {
  const label = SECTION_MAP[pathname] || "PAGE";
  const [phase, setPhase] = useState("entering");
  const doneCalled = useRef(false);

  // Lock body during transition
  useEffect(() => {
    const prevO = document.body.style.overflow;
    const prevP = document.body.style.pointerEvents;
    document.body.style.overflow = "hidden";
    document.body.style.pointerEvents = "none";
    return () => {
      document.body.style.overflow = prevO;
      document.body.style.pointerEvents = prevP || "";
    };
  }, []);

  // State machine
  useEffect(() => {
    if (phase === "entering") {
      const t = setTimeout(() => setPhase("active"), 500);
      return () => clearTimeout(t);
    }
    if (phase === "active") {
      const t = setTimeout(() => setPhase("exiting"), 600);
      return () => clearTimeout(t);
    }
    if (phase === "exiting") {
      const t = setTimeout(() => {
        if (!doneCalled.current) { doneCalled.current = true; onDone(); }
      }, 500);
      return () => clearTimeout(t);
    }
  }, [phase, onDone]);

  // Dynamic word count — ensures text fills the viewport on all devices
  const WORD_COUNT = (() => {
    if (typeof window === "undefined") return 7;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Match CSS clamp: font-size: clamp(3.5rem, 11vw, 13rem)
    const minPx = 3.5 * 16;          // 56px
    const maxPx = 13 * 16;            // 208px
    const prefPx = 0.11 * vw;         // 11vw
    const fontSizePx = Math.min(Math.max(prefPx, minPx), maxPx);
    const rowHeightPx = fontSizePx * 0.92; // line-height: 0.92
    return Math.ceil(vh / rowHeightPx) + 2;
  })();

  const words = Array.from({ length: WORD_COUNT }, (_, i) => (
    <span key={i} className="tx-word">{label}</span>
  ));

  // Enter: curtain slides from above (y: -100% → 0%), Exit: slides up (y: 0% → -100%)
  const curtainY = phase === "exiting" ? "-100%" : "0%";

  // Enter: text hidden (clip all), Active/Exit: text revealed
  const fillReveal = phase === "active" || phase === "exiting" ? "0%" : "100%";

  return (
    <motion.div
      className="tx-overlay"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Subtle scanline texture */}
      <div className="tx-scanlines" aria-hidden="true" />

      {/* ── Curtain — slides top→bottom on enter, bottom→top on exit ── */}
      <motion.div
        className="tx-curtain"
        initial={{ y: "-100%" }}
        animate={{ y: curtainY }}
        transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
      />

      {/* ── Text — hidden during curtain fall, fades in during active, slides up on exit ── */}
      <motion.div
        className="tx-text"
        initial={{ opacity: 0, y: "0%" }}
        animate={{
          opacity: phase === "entering" ? 0 : 1,
          y: phase === "exiting" ? "-100%" : "0%",
        }}
        transition={{
          opacity: { duration: 0.35, ease: "easeOut" },
          y: { duration: 0.5, ease: [0.65, 0, 0.35, 1] },
        }}
      >
        {/* Outline text (always visible once faded in) */}
        <div className="tx-words tx-words--o">
          {words}
        </div>

        {/* Fill text (revealed via clip-path from top) */}
        <motion.div
          className="tx-words tx-words--f"
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: `inset(${fillReveal} 0 0 0)` }}
          transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1], delay: 0.05 }}
        >
          {words}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
